import { motion } from "framer-motion";
import { MapPin, CheckCircle2, Phone } from "lucide-react";
import Reveal from "@/components/Reveal";
import { BIZ, IMAGES } from "@/constants/content";

const ONBOARD = ["Bearings", "Turbines", "O-rings & gaskets", "Chucks", "Scaler tips", "Calibration tools"];

export default function MobileVan() {
  return (
    <section id="van" className="relative overflow-hidden bg-abyss py-20 text-white sm:py-28" data-testid="van-section">
      <div className="blueprint-dark absolute inset-0 opacity-60" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-[420px] w-[420px] rounded-full bg-brandsky/15 blur-[130px]" />
      <div className="pointer-events-none absolute right-0 top-0 h-[360px] w-[360px] rounded-full bg-brandpink/10 blur-[130px]" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="eyebrow text-brandsky">The mobile workshop</p>
            <h2 className="font-display mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-[2.9rem] lg:leading-[1.04]">
              A full repair shop,
              <br />
              parked at your office.
            </h2>
            <p className="mt-6 max-w-xl text-[15.5px] leading-relaxed text-white/70" data-testid="van-copy">
              Across <strong className="font-semibold text-white">Buffalo, Erie and Rochester</strong>, our mobile
              workshop van rolls up to your practice and we work on your equipment right in the parking lot. If we
              have the parts on board, you get your handpiece back the same day — usually within about an hour.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {BIZ.areas.map((a) => (
                <span
                  key={a}
                  data-testid={`van-area-${a.toLowerCase().split(",")[0].replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-[13px] font-semibold text-white/90 backdrop-blur"
                >
                  <MapPin className="h-3.5 w-3.5 text-brandsky" />
                  {a}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.16}>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {["No shipping, no downtime", "Same-day with parts on board", "Free estimates on the spot", "Try equipment before you buy"].map((t) => (
                <li key={t} className="flex items-center gap-2.5 text-[14px] font-medium text-white/85">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-brandleaf" />
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={BIZ.jacob.tel}
                data-testid="van-call-button"
                className="btn-lift inline-flex items-center gap-2.5 rounded-full bg-white px-6 py-3.5 text-[14px] font-bold text-ink hover:bg-brandsky-soft"
              >
                <Phone className="h-4 w-4 text-brandsky-deep" />
                Check van availability
              </a>
              <a
                href="#intake"
                data-testid="van-intake-button"
                className="btn-lift inline-flex items-center gap-2.5 rounded-full border border-white/25 px-7 py-3.5 text-[15px] font-bold text-white hover:border-white/60"
              >
                Book the van
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={0.1}>
            <div className="relative mx-auto max-w-[560px]">
              <motion.div
                whileHover={{ rotate: 0, scale: 1.01 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-[26px] border border-white/10 shadow-[0_40px_90px_rgba(0,0,0,0.45)]"
                style={{ rotate: "-1.2deg" }}
                data-testid="van-image-frame"
              >
                <img
                  src={IMAGES.van}
                  alt="Inside the JSL Dental Repair mobile workshop van — repair workbench with tools"
                  className="aspect-[4/3] w-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-abyss/80 to-transparent" />
                <div className="absolute right-4 top-4 flex items-center gap-2.5">
                  <span className="rounded-full bg-brandleaf px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-white shadow-lg">
                    On the road
                  </span>
                  <span className="text-[12.5px] font-semibold text-white/90 drop-shadow">Mon–Fri · by appointment</span>
                </div>
              </motion.div>

              <div className="absolute -bottom-9 left-4 rounded-2xl border border-white/10 bg-white/[0.07] p-5 backdrop-blur-xl sm:left-8" data-testid="van-onboard-card">
                <p className="eyebrow text-white/50">On board the van</p>
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                  {ONBOARD.map((t) => (
                    <span key={t} className="flex items-center gap-2 text-[13px] font-medium text-white/85">
                      <span className="h-1.5 w-1.5 rotate-45 rounded-[1px] bg-brandsky" />
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}